import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Clinic.css";
import "../styles/global.css";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const PlusIcon = (p) => <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />;
const EditIcon = (p) => <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />;
const TrashIcon = (p) => <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />;
const CloseIcon = (p) => <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />;
const ChevronLeftIcon = (p) => <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />;
const ChevronRightIcon = (p) => <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />;
const CheckIcon = (p) => <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />;
const XCircleIcon = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />;
const CheckCircleIcon = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>} />;
const ClinicIcon = (p) => <Icon {...p} d={<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" /></>} />;
const UploadCloudIcon = (p) => <Icon {...p} d={<><polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" /></>} />;
const GripIcon = (p) => <Icon {...p} d={<><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></>} />;
const PhoneIcon = (p) => <Icon {...p} d={<><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.6 2.23h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></>} />;
const MapPinIcon = (p) => <Icon {...p} d={<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>} />;
const FileTextIcon = (p) => <Icon {...p} d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></>} />;
const StarIcon = (p) => <Icon {...p} d={<><path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" /></>} />;

/* ────────── Custom Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Multi-Select Dropdown ────────── */
const MultiSelect = ({ options, value, onChange, placeholder = "Filter by status…" }) => {
  const [open, setOpen] = useState(false);
  const toggleOption = (opt) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  return (
    <div className="bc-multiselect" style={{ position: "relative" }}>
      <div className="bc-multiselect__trigger" onClick={() => setOpen(!open)} tabIndex={0}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
        aria-haspopup="listbox" aria-expanded={open}>
        <span className="bc-multiselect__label">
          {value.length === 0 ? placeholder : value.map((v) => <span key={v} className="bc-multiselect__tag">{v}</span>)}
        </span>
        <span className="bc-multiselect__caret">
          {value.length > 0 && <button className="bc-multiselect__clear" onClick={clearAll} tabIndex={-1} type="button"><XCircleIcon size={14} /></button>}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}><path d="m6 9 6 6 6-6" /></svg>
        </span>
      </div>
      {open && (
        <div className="bc-multiselect__dropdown" role="listbox" aria-multiselectable="true">
          {options.map((opt) => (
            <div key={opt} className={`bc-multiselect__option ${value.includes(opt) ? "is-selected" : ""}`}
              role="option" aria-selected={value.includes(opt)} onClick={() => toggleOption(opt)}
              tabIndex={0} onKeyDown={(e) => e.key === "Enter" && toggleOption(opt)}>
              <span className="bc-multiselect__check">{value.includes(opt) && <CheckIcon size={12} stroke={2.5} />}</span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── iPhone-style Toggle ────────── */
const ToggleSwitch = ({ checked, onChange, disabled }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled}
    onClick={onChange} className={`dept-toggle ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Click to deactivate" : "Click to activate"}>
    <span className="dept-toggle__thumb" />
  </button>
);

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="bc-pagination">
      <div className="bc-pagination__info">
        <span>
          Showing <b>{totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b>
        </span>
        <select className="bc-pagination__perpage" value={perPage} onChange={(e) => onPerPageChange(Number(e.target.value))}>
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>
      <div className="bc-pagination__pages">
        <button className="bc-pagination__btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} aria-label="Previous page"><ChevronLeftIcon size={16} /></button>
        {pages.map((p, i) =>
          p === "..." ? <span key={`e-${i}`} className="bc-pagination__ellipsis">…</span> :
          <button key={p} className={`bc-pagination__btn ${p === currentPage ? "is-active" : ""}`} onClick={() => onPageChange(p)} aria-current={p === currentPage ? "page" : undefined}>{p}</button>
        )}
        <button className="bc-pagination__btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(currentPage + 1)} aria-label="Next page"><ChevronRightIcon size={16} /></button>
      </div>
    </div>
  );
};

/* ────────── Toast Notifications ────────── */
const SuccessToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--success" role="alert">
    <span className="dept-toast__icon"><CheckCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button"><CloseIcon size={15} /></button>
  </div>
);
const ErrorToast = ({ message, onClose }) => (
  <div className="dept-toast dept-toast--error" role="alert">
    <span className="dept-toast__icon"><XCircleIcon size={20} /></span>
    <span className="dept-toast__msg">{message}</span>
    <button className="dept-toast__close" onClick={onClose} type="button"><CloseIcon size={15} /></button>
  </div>
);

/* ────────── Multi-Image Upload with Drag & Drop Reorder ────────── */
const MultiImageUpload = ({ imageFiles, onFilesChange, existingImages, onRemoveExisting }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const fileInputRef = useRef(null);

  const previewItems = useMemo(() => {
    return imageFiles.map((file) => ({
      type: "new",
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));
  }, [imageFiles]);

  // Cleanup object URLs
  useEffect(() => {
    return () => previewItems.forEach((item) => URL.revokeObjectURL(item.url));
  }, [previewItems]);

  const handleDropZone = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
    if (files.length) onFilesChange([...imageFiles, ...files]);
  }, [imageFiles, onFilesChange]);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    if (files.length) onFilesChange([...imageFiles, ...files]);
    e.target.value = "";
  };

  const removeNewFile = (idx) => {
    const updated = imageFiles.filter((_, i) => i !== idx);
    onFilesChange(updated);
  };

  /* ── Drag reorder for new files ── */
  const handleDragStart = (e, idx) => {
    setDraggingIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(idx);
  };
  const handleDrop = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggingIdx === null || draggingIdx === idx) {
      setDraggingIdx(null);
      setDragOverIdx(null);
      return;
    }
    const reordered = [...imageFiles];
    const [moved] = reordered.splice(draggingIdx, 1);
    reordered.splice(idx, 0, moved);
    onFilesChange(reordered);
    setDraggingIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDraggingIdx(null); setDragOverIdx(null); };

  const totalCount = (existingImages?.length || 0) + imageFiles.length;

  return (
    <div className="multi-upload">
      {/* Drop Zone */}
      <div
        className={`multi-upload__dropzone ${isDragOver ? "is-over" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDropZone}
        onClick={() => fileInputRef.current?.click()}
      >
        <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileInput} style={{ display: "none" }} />
        <div className="multi-upload__drop-icon">
          <UploadCloudIcon size={36} />
        </div>
        <div className="multi-upload__drop-text">
          <strong>Click to upload</strong> or drag & drop images here
          <span>PNG, JPG, WEBP — multiple files supported</span>
        </div>
        {totalCount > 0 && (
          <div className="multi-upload__count-badge">{totalCount} image{totalCount > 1 ? "s" : ""} selected</div>
        )}
      </div>

      {/* Existing images (edit mode) */}
      {existingImages && existingImages.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p className="multi-upload__section-label">Current Images</p>
          <div className="multi-upload__grid">
            {existingImages.map((imgSrc, idx) => (
              <div key={idx} className="multi-upload__thumb existing">
                <img src={imgSrc} alt={`existing-${idx}`} className="multi-upload__thumb-img" />
                <div className="multi-upload__thumb-order">
                  <StarIcon size={10} />
                  {idx + 1}
                </div>
                <button
                  type="button"
                  className="multi-upload__thumb-remove"
                  onClick={() => onRemoveExisting(idx)}
                  title="Remove"
                >
                  <CloseIcon size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New file previews — drag to reorder */}
      {previewItems.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p className="multi-upload__section-label">
            New Images
            <span className="multi-upload__reorder-hint">
              <GripIcon size={12} /> Drag to reorder
            </span>
          </p>
          <div className="multi-upload__grid">
            {previewItems.map((item, idx) => (
              <div
                key={idx}
                className={`multi-upload__thumb new ${draggingIdx === idx ? "is-dragging" : ""} ${dragOverIdx === idx && draggingIdx !== idx ? "is-dragover" : ""}`}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
              >
                <div className="multi-upload__thumb-grip">
                  <GripIcon size={13} />
                </div>
                <img src={item.url} alt={item.name} className="multi-upload__thumb-img" />
                <div className="multi-upload__thumb-order">{idx + 1}</div>
                <div className="multi-upload__thumb-info">
                  <span className="multi-upload__thumb-name">{item.name}</span>
                  <span className="multi-upload__thumb-size">{(item.size / 1024).toFixed(0)} KB</span>
                </div>
                <button
                  type="button"
                  className="multi-upload__thumb-remove"
                  onClick={() => removeNewFile(idx)}
                  title="Remove"
                >
                  <CloseIcon size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────── Main Component ────────── */
const Clinic = () => {
  const [loading, setLoading] = useState(false);
  // Holds ALL clinics fetched from the API (we fetch a large page so client-side paging works)
  const [clinic, setCategories] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [desc, setDesc] = useState("");
  const [mobile, setMobile] = useState("");
  const [status, setStatus] = useState("Active");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType] = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug] = useState(null);

  // Multi-image state
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [togglingSlug, setTogglingSlug] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // ── Custom confirm dialog state ──
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    confirmVariant: "danger",
    onConfirm: null,
  });

  const showConfirm = useCallback(({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));
  }, []);

  const token = localStorage.getItem("authToken");

  /* ── Fetch Clinics ──
     We fetch a large page (per_page=1000) once so client-side pagination, filtering
     and search all work consistently against the full dataset. This keeps the existing
     API contract intact (the API call & params still work the same way).
  */
  const fetchCategories = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/clinic?page=1&per_page=1000`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setCategories(data.data || []);
        }
      })
      .catch((err) => console.error("Error fetching clinics:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCategories(); }, []);

  /* ── Filtered list (search + status) ── */
  const filteredCategories = useMemo(() => {
    let result = clinic;
    if (filterText) {
      const ft = filterText.toLowerCase();
      result = result.filter(
        (d) =>
          d.name?.toLowerCase().includes(ft) ||
          d.slug?.toLowerCase().includes(ft) ||
          d.mobile?.toLowerCase().includes(ft)
      );
    }
    if (statusFilter.length > 0) {
      result = result.filter((d) => statusFilter.includes(d.status));
    }
    return result;
  }, [filterText, statusFilter, clinic]);

  /* ── Reset to first page when filters change ── */
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, statusFilter]);

  /* ── If currentPage becomes out of bounds (e.g. after delete), clamp it ── */
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / perPage));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredCategories.length, perPage, currentPage]);

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4500);
  };

  /* ── Toggle Status ── */
  const handleStatusChange = (slug, currentStatus) => {
    setTogglingSlug(slug);
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    fetch(`${API_BASE_URL}api/clinic/status/${slug}`, {
      method: "GET",
      headers: { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          flashMessage(`Status updated to ${newStatus} successfully!`, "success");
          fetchCategories();
        } else {
          flashMessage("Failed to update status.", "danger");
        }
      })
      .catch(() => flashMessage("Error while updating status.", "danger"))
      .finally(() => setTogglingSlug(null));
  };

  /* ── Save / Update ── */
  const handleSaveClinic = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("mobile", mobile);
    formData.append("is_home", 1);
    formData.append("status", status);
    formData.append("address", address);
    formData.append("desc", desc);

    // Send back the filenames of existing images that are still kept
    // (i.e. not removed via the "x" button). Strip the full URL prefix
    // down to just the filename, since that's what's stored in the DB.
    if (isEditMode) {
      const keptExisting = existingImages
        .filter((img) => !img.includes("noimage.jpg"))
        .map((img) => img.split("/").pop());
      formData.append("existing_images", keptExisting.join(","));
    }

    if (imageFiles && imageFiles.length > 0) {
      for (let i = 0; i < imageFiles.length; i++) {
        formData.append("images[]", imageFiles[i]);
      }
    }

    const url = isEditMode
      ? `${API_BASE_URL}api/clinic/${editSlug}`
      : `${API_BASE_URL}api/clinic`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          setShowModal(false);
          flashMessage(isEditMode ? "Clinic updated successfully!" : "Clinic added successfully!", "success");
          resetForm();
          fetchCategories();
        } else {
          flashMessage("Failed to save clinic.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  const resetForm = () => {
    setName("");
    setMobile("");
    setAddress("");
    setDesc("");
    setStatus("Active");
    setImageFiles([]);
    setExistingImages([]);
    setIsEditMode(false);
    setEditSlug(null);
  };

  /* ── Edit ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditSlug(row.slug);
    setName(row.name);
    setMobile(row.mobile);
    setAddress(row.address);
    setDesc(row.desc);
    setStatus(row.status);
    setImageFiles([]);

    // Parse existing multi-images
    if (row.image) {
      const imgs = row.image.split(",").map(
        (img) => `${API_BASE_URL}public/clinic/${img.trim()}`
      );
      setExistingImages(imgs);
    } else {
      setExistingImages([`${API_BASE_URL}public/noimage.jpg`]);
    }
    setShowModal(true);
  };

  /* ── Delete single (now using custom confirm dialog) ── */
  const handleDelete = (slug) => {
    showConfirm({
      title: "Delete Clinic",
      message: "Are you sure you want to delete this clinic? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/clinic/destroy/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Clinic deleted successfully!", "success");
              fetchCategories();
            } else {
              flashMessage("Failed to delete clinic.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting clinic.", "danger"));
      },
    });
  };

  /* ── Bulk delete (now using custom confirm dialog) ── */
  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    const count = selectedRows.length;
    showConfirm({
      title: "Delete Selected",
      message: `Are you sure you want to delete ${count} selected clinic${count > 1 ? "s" : ""}? This action cannot be undone.`,
      confirmLabel: `Delete ${count} Item${count > 1 ? "s" : ""}`,
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        Promise.all(
          selectedRows.map((row) =>
            fetch(`${API_BASE_URL}api/clinic/destroy/${row.slug}`, {
              method: "GET",
              headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
            }).then((res) => res.json())
          )
        )
          .then(() => {
            flashMessage(`${count} clinic${count > 1 ? "s" : ""} deleted successfully!`, "success");
            setSelectedRows([]);
            setToggleCleared(!toggleCleared);
            fetchCategories();
          })
          .catch(() => flashMessage("Error deleting clinics!", "danger"));
      },
    });
  };

  /* ── Open Add modal ── */
  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  /* ── Pagination helpers ── */
  const totalRows = filteredCategories.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredCategories.slice(start, start + perPage);
  }, [filteredCategories, currentPage, perPage]);

  /* ────────── Table Columns ────────── */
  const columns = useMemo(() => [
    {
      name: "Image",
      cell: (row) => (
        <div className="dept-img-wrap">
          <img
            src={row.image ? `${API_BASE_URL}public/clinic/${row.image.split(",")[0]}` : `${API_BASE_URL}public/noimage.jpg`}
            alt={row.name}
            className="dept-img"
          />
        </div>
      ),
      width: "76px",
      ignoreRowClick: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => <span className="bc-cell-name">{row.name}</span>,
      sortable: true,
      grow: 2,
    },
    {
      name: "Phone",
      selector: (row) => row.mobile,
      cell: (row) => (
        <span className="bc-cell-phone">
          <PhoneIcon size={13} />
          {row.mobile}
        </span>
      ),
      sortable: true,
      grow: 1,
    },
    {
      name: "Slug",
      selector: (row) => row.slug,
      cell: (row) => <span className="bc-slug-pill">{row.slug}</span>,
      sortable: true,
      grow: 2,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <ToggleSwitch
          checked={row.status === "Active"}
          disabled={togglingSlug === row.slug}
          onChange={() => handleStatusChange(row.slug, row.status)}
        />
      ),
      width: "100px",
      ignoreRowClick: true,
    },
    {
      name: "Created",
      selector: (row) => row.created_at,
      cell: (row) => (
        <span className="bc-cell-muted">
          {row.created_at ? new Date(row.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
        </span>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="bc-row-actions">
          <button className="bc-ico-btn edit" onClick={() => handleEdit(row)} title="Edit"><EditIcon size={15} /></button>
          <button className="bc-ico-btn del" onClick={() => handleDelete(row.slug)} title="Delete"><TrashIcon size={15} /></button>
        </div>
      ),
      width: "110px",
      right: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [togglingSlug]);

  /* ────────── DataTable Custom Styles ────────── */
  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: {
      style: { background: "#f0fbf6", borderTopLeftRadius: "12px", borderTopRightRadius: "12px", borderBottom: "1px solid #d6efe2", minHeight: "52px" },
    },
    headCells: {
      style: { fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5b6b66", paddingLeft: "16px", paddingRight: "16px" },
    },
    rows: {
      style: { minHeight: "72px", fontSize: "14px", color: "#1f2a37", borderBottom: "1px solid #eef4f1 !important", transition: "background 0.18s ease" },
      highlightOnHoverStyle: { background: "#f6fcf9", borderBottomColor: "#e3f0e9", outline: "none" },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  };

  /* ────────── Render ────────── */
  return (
    <>
      <div id="layout-wrapper">
        <Header />

        {loading && (
          <div className="bc-overlay">
            <div className="bc-spinner" />
          </div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="bc-shell">

              {/* ── Page Header ── */}
              <div className="bc-head">
                <div>
                  <h2>Clinics</h2>
                  <p>Manage and organise all your clinics in one place.</p>
                </div>
                <button type="button" className="bc-add-btn" onClick={openAddModal}>
                  <PlusIcon size={18} />
                  Add Clinic
                </button>
              </div>

              {/* ── Toast ── */}
              {messagealert && messageType === "success" && (
                <SuccessToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}
              {messagealert && messageType === "danger" && (
                <ErrorToast message={messagealert} onClose={() => setMessageAlert("")} />
              )}

              {/* ── Main Card ── */}
              <div className="bc-card">
                {/* Toolbar */}
                <div className="bc-card-top">
                  <div className="bc-card-top-left">
                    <div className="bc-search-box">
                      <SearchIcon size={17} />
                      <input
                        type="text"
                        placeholder="Search by name, phone or slug…"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </div>
                    <MultiSelect
                      options={["Active", "InActive"]}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      placeholder="Filter by status…"
                    />
                  </div>
                  <div className="bc-total-badge">
                    <ClinicIcon size={16} />
                    Total:&nbsp;<b>{filteredCategories.length}</b>
                  </div>
                </div>

                {/* Bulk bar */}
                {selectedRows.length > 0 && (
                  <div className="bc-bulk-bar">
                    <div className="bc-bulk-count">
                      <span className="bc-num-badge">{selectedRows.length}</span>
                      clinic{selectedRows.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="bc-bulk-actions">
                      <button className="bc-bulk-btn ghost" type="button" onClick={() => { setSelectedRows([]); setToggleCleared(!toggleCleared); }}>Clear</button>
                      <button className="bc-bulk-btn danger" type="button" onClick={handleBulkDelete}>
                        <TrashIcon size={14} /> Delete Selected
                      </button>
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <DataTable
                  columns={columns}
                  data={pagedData}
                  customStyles={tableStyles}
                  highlightOnHover
                  responsive
                  striped
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                  clearSelectedRows={toggleCleared}
                  noDataComponent={
                    <div className="bc-empty">
                      <ClinicIcon size={40} />
                      <p>No clinics found</p>
                      <span>Try adjusting your search or add a new clinic</span>
                    </div>
                  }
                />

                {/* Pagination */}
                {filteredCategories.length > 0 && (
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={(n) => { setPerPage(n); setCurrentPage(1); }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ────────── Add / Edit Modal ────────── */}
          {showModal && (
            <div
              className="bc-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true"
              role="dialog"
              aria-labelledby="clinicModalTitle"
            >
              <div className="bc-modal">
                {/* Header */}
                <div className="bc-modal__header">
                  <div className="bc-modal__header-left">
                    <div className="bc-modal__icon">
                      <ClinicIcon size={20} />
                    </div>
                    <div>
                      <p className="bc-modal__title" id="clinicModalTitle">
                        {isEditMode ? "Edit Clinic" : "Add New Clinic"}
                      </p>
                      <p className="bc-modal__subtitle">
                        {isEditMode ? "Update the details for this clinic" : "Fill in the details to create a new clinic"}
                      </p>
                    </div>
                  </div>
                  <button type="button" className="bc-modal__close" onClick={() => setShowModal(false)} aria-label="Close modal">
                    <CloseIcon size={16} />
                  </button>
                </div>

                {/* Body — scrollable */}
                <form onSubmit={handleSaveClinic}>
                  <div className="bc-modal__body">

                    {/* Name */}
                    <div className="bc-form-group">
                      <label className="bc-form-label" htmlFor="clinicName">
                        Clinic Name <span>*</span>
                      </label>
                      <input
                        type="text"
                        id="clinicName"
                        className="bc-form-control"
                        placeholder="e.g. Apollo Clinic - Anna Nagar"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>

                    {/* Phone + Address (2-col) */}
                    <div className="bc-form-row">
                      <div className="bc-form-group" style={{ marginBottom: 0 }}>
                        <label className="bc-form-label bc-form-label-icon" htmlFor="clinicPhone">
                          <PhoneIcon size={13} /> Phone <span>*</span>
                        </label>
                        <input
                          type="text"
                          id="clinicPhone"
                          className="bc-form-control"
                          placeholder="e.g. +91 98765 43210"
                          value={mobile}
                          onChange={(e) => setMobile(e.target.value)}
                          required
                        />
                      </div>
                      <div className="bc-form-group" style={{ marginBottom: 0 }}>
                        <label className="bc-form-label bc-form-label-icon" htmlFor="clinicAddress">
                          <MapPinIcon size={13} /> Address <span>*</span>
                        </label>
                        <input
                          type="text"
                          id="clinicAddress"
                          className="bc-form-control"
                          placeholder="e.g. 12, Main Street, Chennai"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="bc-form-group" style={{ marginTop: 18 }}>
                      <label className="bc-form-label bc-form-label-icon" htmlFor="clinicDesc">
                        <FileTextIcon size={13} /> Description 
                      </label>
                     <textarea
                          id="clinicDesc"
                          className="bc-form-control"
                          placeholder="Brief description about the clinic…"
                          value={desc}
                          onChange={(e) => setDesc(e.target.value)}
                          rows={3}
                          style={{ resize: "vertical", lineHeight: 1.6 }}
                        />
                    </div>

                    {/* Multi-image upload with drag & drop reorder */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">
                        Clinic Images
                        <span style={{ color: "#9aa8a2", fontWeight: 400, marginLeft: 6, fontSize: 11 }}>
                          — upload multiple, drag to reorder
                        </span>
                      </label>
                      <MultiImageUpload
                        imageFiles={imageFiles}
                        onFilesChange={setImageFiles}
                        existingImages={existingImages}
                        onRemoveExisting={(idx) => {
                          setExistingImages(existingImages.filter((_, i) => i !== idx));
                        }}
                      />
                    </div>

                    {/* Status Toggle */}
                    <div className="bc-form-group">
                      <label className="bc-form-label">Status</label>
                      <div className="dept-status-row">
                        <div>
                          <div className="dept-status-row-label">Active Status</div>
                          <div className="dept-status-row-hint">Toggle to set this clinic as active or inactive</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span className={`dept-status-indicator ${status === "Active" ? "active" : "inactive"}`}>
                            {status === "Active" ? "Active" : "Inactive"}
                          </span>
                          <ToggleSwitch
                            checked={status === "Active"}
                            onChange={() => setStatus(status === "Active" ? "InActive" : "Active")}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="bc-modal__footer">
                    <button type="button" className="bc-btn-cancel" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="bc-btn-submit">
                      {isEditMode ? (
                        <><EditIcon size={15} /> Update Clinic</>
                      ) : (
                        <><PlusIcon size={15} /> Add Clinic</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ────────── Custom Confirm Dialog (replaces window.confirm) ────────── */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Clinic;